/**
 * Background Overlay — neutral wash mapping (single shared home).
 *
 * The `backgroundOverlayStrength` config field (0–1) controls a neutral wash
 * layer over a surface's background image: black in dark mode, white in light
 * mode, decoupled from the school's theme palette. This module is the ONE place
 * the strength→opacity curve, the per-mode balance, and the neutral colour live,
 * so both mobile renderers (home + tile screens) and the web editor previews
 * resolve the exact same look from a single stored value.
 *
 * The curve and per-mode balance are deliberately simple and centralised here
 * (KTD5): tuning them later means editing this file alone, and every surface
 * follows. `strength = 0` always yields opacity `0` (image at full strength,
 * no wash — Miami's ask, AE1).
 */

/**
 * Colour-scheme values the wash mapping understands. Only `'dark'` is treated
 * specially; every other value (including React Native's `'unspecified'` and
 * `null`/`undefined`) falls to the light branch. Widened to any string so
 * callers can pass RN's `ColorSchemeName` directly.
 */
export type OverlayColorScheme = 'light' | 'dark' | (string & {}) | null | undefined

/**
 * Maximum wash opacity at `strength = 1`, per mode. Dark mode tolerates a
 * slightly stronger black wash than light mode does a white one before the
 * photo is fully lost — this asymmetry is the per-mode "balance" that lets a
 * single stored value read sensibly in both modes (R2/R3). Both are "much
 * higher than today" so the top of the slider is a genuinely strong dim.
 */
export const OVERLAY_WASH_MAX_OPACITY = {
  dark: 0.9,
  light: 0.82
} as const

/**
 * Approximate today's-look strength — where the unset surface's faded image
 * roughly sits on the 0–1 scale. Used by the editors as the default-marker
 * position and the "reset to default" reference. It only APPROXIMATES today's
 * look (KTD3): the unset/null state reproduces today exactly via a separate
 * render path; this marker is the on-slider hint of where that lands.
 */
export const TODAYS_LOOK_OVERLAY_STRENGTH = 0.85

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n)

/**
 * Map a 0–1 overlay strength to the wash layer's opacity for the active mode.
 * Linear within each mode, scaled by the per-mode maximum. `strength = 0` → `0`.
 */
export function strengthToWashOpacity(strength: number, colorScheme: OverlayColorScheme): number {
  if (!Number.isFinite(strength)) return 0
  const max = colorScheme === 'dark' ? OVERLAY_WASH_MAX_OPACITY.dark : OVERLAY_WASH_MAX_OPACITY.light
  return clamp01(strength) * max
}

/** The neutral wash colour for the active mode — black in dark, white in light. */
export function neutralWashColor(colorScheme: OverlayColorScheme): string {
  return colorScheme === 'dark' ? '#000000' : '#FFFFFF'
}

/** A resolved neutral wash layer: the colour + opacity to paint over the image. */
export interface ResolvedWashLayer {
  color: string
  opacity: number
}

export interface BackgroundOverlayInput {
  /** The stored 0–1 strength, or null/undefined when the surface is unset. */
  strength: number | null | undefined
  /** Whether the surface actually has a background image to wash (R13). */
  hasBackgroundImage: boolean
  /** Active device colour scheme. */
  colorScheme: OverlayColorScheme
  /**
   * The surface's existing default image opacity — the "today's-look" fade used
   * on the unset path (home: 0.15 dark / 0.3 light; tile: same). Returned as
   * `imageOpacity` when unset so back-compat is exact (R8).
   */
  defaultImageOpacity: number
}

export interface BackgroundOverlayResult {
  /**
   * Opacity to render the background IMAGE at. When the control is set (and an
   * image exists) this is `1` — the image shows at full strength and the wash
   * does the dimming, subsuming the old hardcoded fade (R11). When unset it is
   * `defaultImageOpacity` — today's faded look, unchanged (R8).
   */
  imageOpacity: number
  /** The neutral wash to paint over the image, or `null` for no wash. */
  wash: ResolvedWashLayer | null
}

/**
 * Resolve how a surface should render its background given the overlay control.
 * The ONE decision both mobile renderers (home + tile) and the web previews run,
 * so "set → full-strength image + neutral wash; unset → today's fade; 0 → no
 * wash; no image → nothing" is defined in exactly one tested place (KTD2/KTD3).
 */
export function resolveBackgroundOverlay(input: BackgroundOverlayInput): BackgroundOverlayResult {
  const { strength, hasBackgroundImage, colorScheme, defaultImageOpacity } = input

  // Unset (null/undefined/non-finite) OR no image to wash → reproduce today's
  // look exactly: the default fade, no wash layer (R8, R13).
  if (strength == null || !Number.isFinite(strength) || !hasBackgroundImage) {
    return { imageOpacity: defaultImageOpacity, wash: null }
  }

  // Set → full-strength image; the wash carries all the dimming (R10/R11).
  const opacity = strengthToWashOpacity(strength, colorScheme)
  return {
    imageOpacity: 1,
    // strength 0 → opacity 0 → no wash at all (AE1, Miami).
    wash: opacity > 0 ? { color: neutralWashColor(colorScheme), opacity } : null
  }
}
